import { useEffect, useMemo } from "react"
import { Outlet, useNavigate, useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import fp from "lodash/fp"

import { useDeviceQuery } from "features/api"
import useDocumentTitle from "helpers/hooks/useDocumentTitle"
import PreviousPage from "widgets/common/PreviousPage"
import Spinner from "widgets/common/Spinner"
import DeviceSummary from "widgets/device/DeviceSummary"
import useDevices from "helpers/hooks/useDevices"
import { DEFAULT_TARGET } from "helpers/utils/constants"
import DashboardDatePicker from "widgets/common/dashboard/DashboardDatePicker"
import ScrollToTopButton from "widgets/common/ScrollToTopButton"
import ExitZoomModeButton from "widgets/common/ExitZoomModeButton"
import { getDashboardTabsConfig } from "helpers/config/tabs/helpers"
import useDateRange from "helpers/hooks/useDateRange"
import LinkTabsSection from "widgets/common/LinkTabsSection"
import StickyReportingStatus from "widgets/device/StickyReportingStatus"
import useAlertSettings from "helpers/hooks/useAlertSettings"
import useAlertSettingsWStates from "helpers/hooks/useAlertSettingsStates"
import { entityHasTriggeredAlerts } from "helpers/utils/alerts"

function Device() {
  const { t } = useTranslation()
  const { getDateRangeTools, arePlotsLoading } = useDateRange()
  const {
    dateRange: deviceDateRange,
    dispatchDateRange: dispatchDeviceDateRange,
    searchParams: deviceSearchParams,
  } = getDateRangeTools("device")
  const { devsWRelsById } = useDevices()
  const navigate = useNavigate()
  const { orgId, id } = useParams()
  const {
    data: device,
    isSuccess: deviceIsSuccess,
    isLoading: deviceIsLoading,
    isError,
  } = useDeviceQuery({ id: Number(id) })

  useDocumentTitle(device?.name ?? t("pageTitles.DEVICE"))

  const groups = useMemo(
    () => fp.getOr([], [Number(id), "groups"], devsWRelsById),
    [devsWRelsById, id],
  )
  const isWrongOrg = useMemo(
    () => device && device.enterprise_id !== Number(orgId),
    [device, orgId],
  )
  const { getDeviceAlertSettingsWStatesByEntityId } = useAlertSettingsWStates()
  const { orgActiveDevicesAlertSettingsById } = useAlertSettings()

  const hasTriggeredAlerts = useMemo(
    () =>
      entityHasTriggeredAlerts(
        getDeviceAlertSettingsWStatesByEntityId(Number(id)),
        orgActiveDevicesAlertSettingsById,
      ),
    [getDeviceAlertSettingsWStatesByEntityId, id, orgActiveDevicesAlertSettingsById],
  )

  const dashboardTabsConfig = useMemo(
    () =>
      device &&
      getDashboardTabsConfig(deviceSearchParams, device.device_type, device.settings),
    [device, deviceSearchParams],
  )

  useEffect(() => {
    if (isError) {
      navigate(DEFAULT_TARGET)
    } else if (isWrongOrg && device) {
      const url = `/orgs/${device.enterprise_id}/devices/${device.less_id}`
      navigate(url)
    } else if (isWrongOrg) {
      navigate(DEFAULT_TARGET)
    }
  }, [isError, isWrongOrg, navigate, device])

  return (
    <>
      {deviceIsLoading || isWrongOrg ? (
        <Spinner styles={{ my: 2 }} />
      ) : (
        deviceIsSuccess && (
          <>
            <StickyReportingStatus device={device} />
            <PreviousPage />
            <DeviceSummary
              device={device}
              groups={groups}
              actions={
                <DashboardDatePicker
                  dateRange={deviceDateRange}
                  dispatchDateRange={dispatchDeviceDateRange}
                  isFetching={arePlotsLoading}
                />
              }
            />
            {dashboardTabsConfig && (
              <LinkTabsSection
                tabsConfig={dashboardTabsConfig}
                badges={{ "tabs.ALERTS": hasTriggeredAlerts }}
              />
            )}
            <Outlet context={{ id, device }} />
            <ExitZoomModeButton entity="device" />
            <ScrollToTopButton />
          </>
        )
      )}
    </>
  )
}

export default Device
